import { useEffect } from 'react';

import { ResponsiveLine } from '@nivo/line';
import { t } from 'i18next';
import { toast } from 'react-toastify';
import { useQuery } from '@apollo/client';

import { Tooltip } from '../Tooltips/Tooltip';

import { useChartLegendColor } from '../../context/Theme';

import {
  getFormattedValue,
  getMarkersAndTickers,
} from '../../helpers/functions';

import { LIST_EVENTS } from '../../http/apollo-queries';

import { EventsList, LineChartData } from './types';

export const ChartLine = ({
  canShowBlackSwan = false,
  data,
  height = 96,
  hideAxis = false,
  max,
  min,
  suffix = '',
  useVariation = false,
  usePercentage = false,
  useCurrentPoints = true,
  setBlackSwanToggleState,
}: {
  canShowBlackSwan?: boolean;
  data: LineChartData[];
  height?: string | number;
  hideAxis?: boolean;
  max?: number;
  min?: number;
  suffix?: string;
  useVariation?: boolean;
  usePercentage?: boolean;
  useCurrentPoints?: boolean;
  setBlackSwanToggleState?(newState?: boolean | undefined): void;
}) => {
  const chartTheme = useChartLegendColor();

  const { data: { listEvents: { items: events = [] } = {} } = {} } =
    useQuery<EventsList>(LIST_EVENTS);

  const { markers, tickValues } = getMarkersAndTickers(
    data,
    canShowBlackSwan ? events : []
  );

  useEffect(() => {
    if (canShowBlackSwan && markers.length === 0) {
      toast.warn(t('strategies.warnings.blackSwan'));
      setBlackSwanToggleState?.(false);
    }
  }, [canShowBlackSwan]);

  max === undefined &&
    (max = data.reduce(
      (max, d) =>
        d.data.length > 0 ? Math.max(max, ...d.data.map(({ y }) => y)) : max,
      -Infinity
    ));

  min === undefined &&
    (min = data.reduce(
      (m, d) =>
        d.data.length > 0 ? Math.min(m, ...d.data.map(({ y }) => y)) : m,
      Infinity
    ));

  return (
    <div className={`flex h-${height}`}>
      {data.length !== 0 ? (
        <ResponsiveLine
          animate={false}
          theme={chartTheme}
          colors={{ datum: 'color' }}
          enablePoints={false}
          enableGridX={false}
          data={data}
          margin={{ top: 0, right: 10, bottom: 30, left: hideAxis ? 10 : 70 }}
          markers={markers}
          xScale={{
            type: 'time',
            format: '%Y-%m-%d',
            useUTC: false,
            precision: 'day',
          }}
          xFormat="time:%Y-%m-%d"
          yScale={{
            type: 'linear',
            stacked: false,
            max: max + (data.length > 1 ? 0.5 : max / 10),
            min: min + (data.length > 1 ? -0.5 : -min / 10),
          }}
          axisLeft={{
            format: (val) => {
              const { formattedValue, suffix: newSuffix } = getFormattedValue(
                +val,
                usePercentage ? '%' : suffix
              );

              return hideAxis ? null : `${formattedValue}${newSuffix}`;
            },
          }}
          axisBottom={hideAxis ? null : { format: '%d %b', tickValues }}
          curve={'monotoneX'}
          useMesh={true}
          enableSlices="x"
          layers={[
            ...(useCurrentPoints ? [CustomPoints] : []),
            'grid',
            'markers',
            'axes',
            'areas',
            'crosshair',
            'lines',
            'points',
            'slices',
            'mesh',
            'legends',
          ]}
          sliceTooltip={({ slice }) => (
            <Tooltip
              useVariation={useVariation}
              usePercentage={usePercentage}
              data={data}
              slice={slice}
              suffix={suffix}
              events={markers.map(({ date, title }) => ({ date, title }))}
            />
          )}
        />
      ) : (
        <div className="m-5">
          No data available! <br />
          Add one or more strategies to continue
        </div>
      )}
    </div>
  );
};

function CustomPoints(props) {
  const { currentSlice } = props;
  if (!currentSlice) return null;

  return currentSlice.points.map((point) => {
    return (
      <svg key={point.id} fill={point.color}>
        <circle cx={point.x} cy={point.y} r="5" />
      </svg>
    );
  });
}
